/**
 * Streaming battle adapter. Replaces the Phase 8 vertical-slice runBattle()
 * (which played both sides via RandomPlayerAI and returned a complete event
 * log up-front). Now p1 is interactive: the BattleScene calls
 * `nextP1Choice()` to get the available moves, prompts the user, and submits
 * via `chooseMove()`. p2 is still RandomPlayerAI.
 *
 * The events + choices flow as two independent async iterators backed by
 * AsyncQueues. The omniscient stream pumps battle events into the event
 * queue; the p1 stream pumps `|request|` lines into the choice queue. The
 * BattleScene drives both concurrently.
 *
 * @pkmn/sim is dynamic-imported so the ~6 MB sim chunk only enters the
 * bundle on the first battle (same lazy pattern as data/dex.ts).
 */
import type { BattleEvent, BattleSetup, MoveChoice, TeamSlot, TurnRequest } from './types';
import { parseLine } from './parseLine';
import type { PartyMemberStatus } from './statusVisuals';

export interface InteractiveBattle {
  /** Async iterator over BattleEvents. Last event is `battleEnd`. */
  events(): AsyncIterableIterator<BattleEvent>;
  /** Resolves with the next turn's full request (moves + team state), or
   *  null when the battle ends with no further choices. */
  nextP1Choice(): Promise<TurnRequest | null>;
  /** Submit p1's chosen 1-indexed move slot. */
  chooseMove(slot: number): void;
  /** Submit a switch to p1's 1-indexed party slot. */
  chooseSwitch(slot: number): void;
  /** PR 3 EXP 5: snapshot p1's per-slot status condition strings,
   *  sim-slot-indexed. Read at battleEnd / runFromBattle so
   *  BattleScene can flush each member's status back to runState.
   *  Defensive on sim-shape mismatch — returns an empty array on
   *  internal-access failure, which the caller treats as "no status
   *  flush this battle" (the prior values stay). */
  snapshotP1Statuses(): PartyMemberStatus[];
  /** G11 (bag-in-battle): apply an item's effect to p1's party and
   *  consume the turn — the opponent moves, p1 does nothing. Returns
   *  false (and consumes NOTHING — no turn, no item) when the effect
   *  is a no-op on the target (full HP / non-matching status / fainted)
   *  or on sim-shape mismatch. On true, the caller must NOT also
   *  submit a move this turn; await the next choice as usual. */
  useItemTurn(args: UseItemTurnArgs): boolean;
}

export interface UseItemTurnArgs {
  /** Sim party-order index (0-based) of the target — matches the
   *  TeamSlot ordering in TurnRequest.team. */
  targetIndex: number;
  effect:
    | { kind: 'heal'; amount: number }
    | { kind: 'cure'; cures: ReadonlyArray<string> };
}

class AsyncQueue<T> {
  private items: T[] = [];
  private waiters: Array<(t: T | null) => void> = [];
  private finished = false;

  push(item: T): void {
    const w = this.waiters.shift();
    if (w) w(item);
    else this.items.push(item);
  }

  finish(): void {
    this.finished = true;
    while (this.waiters.length > 0) this.waiters.shift()!(null);
  }

  pull(): Promise<T | null> {
    if (this.items.length > 0) return Promise.resolve(this.items.shift() as T);
    if (this.finished) return Promise.resolve(null);
    return new Promise<T | null>((resolve) => this.waiters.push(resolve));
  }

  async *iter(): AsyncIterableIterator<T> {
    while (true) {
      const item = await this.pull();
      if (item === null) return;
      yield item;
    }
  }
}

export async function startInteractiveBattle(setup: BattleSetup): Promise<InteractiveBattle> {
  const { BattleStreams, RandomPlayerAI, Teams } = await import('@pkmn/sim');

  const stream = new BattleStreams.BattleStream();
  const streams = BattleStreams.getPlayerStreams(stream);

  const team1Packed = Teams.pack(setup.p1.team);
  const team2Packed = Teams.pack(setup.p2.team);

  void streams.omniscient.write(
    `>start ${JSON.stringify({ formatid: 'gen3customgame', seed: setup.seed })}\n` +
      `>player p1 ${JSON.stringify({ name: setup.p1.name, team: team1Packed })}\n` +
      `>player p2 ${JSON.stringify({ name: setup.p2.name, team: team2Packed })}`,
  );

  // Phase 12 M1: inject the player's stored HP into sim's internal state
  // before the battle's first |request| fires. We can't ship current HP
  // through PokemonSet (sim's public protocol has no slot for it), so we
  // reach past the protocol and mutate stream.battle.sides[0].pokemon[i].hp
  // directly. Fragility: depends on @pkmn/sim's internal Pokemon shape; if
  // a future bump breaks it, the bump-and-test ritual (TODOs #13) catches
  // it via the hp-persistence test. p2 (wild + trainer opponents) always
  // starts at full HP — we don't carry their HP across battles.
  if (setup.p1.startingHp) {
    const battle = (stream as unknown as { battle: any }).battle;
    const p1Side = battle?.sides?.[0];
    if (p1Side?.pokemon) {
      for (const mon of p1Side.pokemon as Array<{ species?: { id?: string }; speciesid?: string; hp: number; maxhp: number }>) {
        const speciesId = (mon.species?.id ?? mon.speciesid ?? '').toLowerCase();
        const desired = setup.p1.startingHp[speciesId];
        if (desired === undefined) continue;
        // Clamp to [0, maxhp] defensively.
        mon.hp = Math.max(0, Math.min(desired, mon.maxhp));
      }
    }
  }

  // PR 3 EXP 5c: inject the player's stored persistent status condition
  // into sim's internal pokemon[i].status string (symmetric to the HP
  // injection above). Same fragility surface — depends on the internal
  // shape — but the parallel `status-persistence.test.ts` CI gate
  // catches sim version drift. Wrapped in try/catch per eng review
  // Issue 2A 2026-05-13: a sim-shape mismatch logs a console.warn and
  // falls back to no-status (the legacy behavior) rather than throwing
  // mid-battle-start.
  if (setup.p1.startingStatus) {
    try {
      const battle = (stream as unknown as { battle: any }).battle;
      const p1Side = battle?.sides?.[0];
      if (p1Side?.pokemon) {
        const monsByOrder = p1Side.pokemon as Array<{ status: string }>;
        for (let i = 0; i < monsByOrder.length; i++) {
          const desired = setup.p1.startingStatus[i];
          if (desired === undefined) continue;
          // null / '' → no status. Sim represents healthy as ''.
          monsByOrder[i]!.status = desired ?? '';
        }
      } else {
        throw new Error('p1Side.pokemon missing');
      }
    } catch (err) {
      console.warn('[battle] status inject shape mismatch:', err);
    }
  }

  const p2 = new RandomPlayerAI(streams.p2, { seed: setup.p2.aiSeed });
  void p2.start();

  const eventQueue = new AsyncQueue<BattleEvent>();
  const choiceQueue = new AsyncQueue<TurnRequest>();

  const ctx = { p1Name: setup.p1.name, p2Name: setup.p2.name };

  // G11 lifecycle flags. `suppressPhantomLines` mutes the phantom
  // Splash's protocol lines (|move| + |-nothing) from the UI event
  // stream for the one turn an item is used — BattleScene shows its
  // own "used a POTION" beat instead. `phantomCleanupPending` strips
  // the phantom moveSlot at the next |request| so neither the move
  // chips nor any later turn ever see a 5th move.
  let suppressPhantomLines = false;
  let phantomCleanupPending = false;

  // Drain omniscient stream → events
  void (async () => {
    try {
      for await (const chunk of streams.omniscient) {
        for (const line of chunk.split('\n')) {
          if (suppressPhantomLines) {
            if (line.includes('|Splash|') || line.includes('|-nothing')) {
              continue;
            }
            if (line.startsWith('|turn|')) suppressPhantomLines = false;
          }
          const event = parseLine(line, ctx);
          if (!event) continue;
          eventQueue.push(event);
          if (event.type === 'battleEnd') {
            eventQueue.finish();
            choiceQueue.finish();
            return;
          }
        }
      }
    } finally {
      eventQueue.finish();
      choiceQueue.finish();
    }
  })();

  // Drain p1 stream → choice prompts.
  // Showdown emits |request|<json> at the start of every turn. The JSON's
  // `wait: true` means p1 isn't being asked to choose this turn;
  // `forceSwitch: [true]` is a forced switch (after a faint with reserves).
  // For normal move requests the JSON has `active: [{ moves: [...] }]`.
  // request.side.pokemon[] always has the full team (active + reserves) —
  // we project it into TeamSlot[] for the switch menu.
  void (async () => {
    for await (const chunk of streams.p1) {
      for (const line of chunk.split('\n')) {
        if (!line.startsWith('|request|')) continue;
        const json = line.slice('|request|'.length);
        if (!json) continue;
        let request: unknown;
        try {
          request = JSON.parse(json) as unknown;
        } catch {
          continue;
        }
        const r = request as {
          wait?: boolean;
          forceSwitch?: boolean[] | boolean;
          active?: Array<{
            moves?: Array<{
              id: string;
              move: string;
              pp: number;
              maxpp: number;
              disabled?: boolean;
            }>;
          }>;
          side?: {
            pokemon?: Array<{
              ident: string;
              details: string;
              condition: string;
              active: boolean;
            }>;
          };
        };
        if (r.wait) continue;

        // G11 cleanup: the request emitted at the end of an item turn
        // still carries the phantom Splash slot (the sim builds it from
        // moveSlots before we can strip). Pop the real slot now and
        // remember to filter this one projection below.
        const stripPhantom = phantomCleanupPending;
        if (phantomCleanupPending) {
          phantomCleanupPending = false;
          try {
            const battle = (stream as unknown as { battle: any }).battle;
            const activeMon = battle?.sides?.[0]?.active?.[0];
            if (activeMon?.moveSlots) {
              activeMon.moveSlots = (
                activeMon.moveSlots as Array<{ id: string }>
              ).filter((s) => s.id !== 'splash');
            }
          } catch (err) {
            console.warn('[battle] phantom-slot cleanup failed:', err);
          }
        }

        // Project team state. `details` is "Species, Lv, Gender" (M/F/empty).
        // `condition` is "current/max" or "current fnt" when fainted, or
        // "0 fnt" exactly. We accept either form.
        const team: TeamSlot[] = (r.side?.pokemon ?? []).map((p, i) => {
          const detailParts = p.details.split(',').map((s) => s.trim());
          const speciesRaw = detailParts[0] ?? '';
          const levelStr = detailParts.find((s) => s.startsWith('L')) ?? 'L1';
          const level = parseInt(levelStr.slice(1), 10) || 1;
          const cond = p.condition;
          const fainted = cond.includes('fnt') || cond.startsWith('0 ');
          let hpCurrent = 0;
          let hpMax = 1;
          if (!fainted) {
            const slash = cond.split('/');
            hpCurrent = parseInt(slash[0] ?? '0', 10);
            hpMax = parseInt(slash[1] ?? '1', 10);
          }
          return {
            slot: i + 1,
            species: speciesRaw.toLowerCase(),
            displayName: speciesRaw,
            level,
            hpCurrent,
            hpMax,
            active: p.active,
            fainted,
          };
        });

        // Detect forceSwitch (sim sends an array; truthy means slot must switch).
        const forceSwitch =
          Array.isArray(r.forceSwitch) ? !!r.forceSwitch[0] : !!r.forceSwitch;

        const active = r.active?.[0];
        // `stripPhantom` hides the phantom Splash from the one request
        // that carries it (no scope species learns Splash, so filtering
        // by id on that single projection is safe).
        const moves: MoveChoice[] = (active?.moves ?? [])
          .filter((m) => !(stripPhantom && m.id === 'splash'))
          .map((m, i) => ({
            slot: i + 1,
            id: m.id,
            name: m.move,
            pp: m.pp,
            maxpp: m.maxpp,
            disabled: !!m.disabled,
          }));

        // Skip requests that have neither moves nor a forceSwitch — sim
        // sometimes emits empty requests during transitions.
        if (!forceSwitch && moves.length === 0) continue;

        choiceQueue.push({ moves, team, forceSwitch });
      }
    }
  })();

  /** PR 3 EXP 5b: read p1's per-slot status condition strings from
   *  sim's internal state. Same fragility surface as the injection;
   *  the matching `status-persistence.test.ts` CI gate catches drift.
   *  Returns an empty array on internal-access failure — caller treats
   *  this as "no flush" and the prior runState values stay. */
  function snapshotP1Statuses(): PartyMemberStatus[] {
    try {
      const battle = (stream as unknown as { battle: any }).battle;
      const p1Side = battle?.sides?.[0];
      const mons = p1Side?.pokemon as Array<{ status?: string }> | undefined;
      if (!mons) throw new Error('p1Side.pokemon missing');
      return mons.map((mon) => {
        const raw = (mon.status ?? '').toLowerCase();
        if (
          raw === 'psn' ||
          raw === 'tox' ||
          raw === 'par' ||
          raw === 'brn' ||
          raw === 'slp' ||
          raw === 'frz'
        ) {
          return raw;
        }
        return null;
      });
    } catch (err) {
      console.warn('[battle] status snapshot shape mismatch:', err);
      return [];
    }
  }

  /** G11 — the item turn. gen3customgame's protocol has no trainer
   *  "item" choice, so the mechanism is the third documented use of
   *  the post-`>start` internal-mutation surface (after HP + status
   *  injection; same TODOs #13 bump-and-test fragility, gated by
   *  bag-injection.test.ts):
   *
   *    1. Apply the effect by mutating side.pokemon[targetIndex]
   *       directly (reserves included — they matter on switch-in).
   *    2. Give the ACTIVE mon a phantom Splash moveSlot and submit
   *       `>p1 move splash`. The sim resolves a REAL turn: p1's move
   *       does nothing, p2's move runs with authentic math — damage
   *       rolls, secondary procs, and end-of-turn poison ticking
   *       against the healed HP. Replay-deterministic: the choice
   *       string is part of the input trace like any other.
   *    3. The phantom's protocol lines are muted from the UI stream
   *       (suppressPhantomLines); the slot is popped at the next
   *       |request| drain (phantomCleanupPending). Real moves' PP is
   *       untouched — the phantom carries its own pp.
   *
   *  Validation here is a defensive mirror of bagUse.ts rules: the UI
   *  layer already blocks no-ops, but a false return must never cost
   *  the player a turn or an item. */
  function useItemTurn(args: UseItemTurnArgs): boolean {
    try {
      const battle = (stream as unknown as { battle: any }).battle;
      const side = battle?.sides?.[0];
      const target = side?.pokemon?.[args.targetIndex] as
        | {
            hp: number;
            maxhp: number;
            status?: string;
            clearStatus?: () => void;
          }
        | undefined;
      if (!target || target.hp <= 0) return false;

      if (args.effect.kind === 'heal') {
        if (target.hp >= target.maxhp) return false;
        target.hp = Math.min(target.hp + args.effect.amount, target.maxhp);
      } else {
        const current = (target.status ?? '').toLowerCase();
        if (!current || !args.effect.cures.includes(current)) return false;
        // Prefer the sim's own cure (resets toxic counters etc.);
        // raw string clear is the fallback for shape drift.
        if (typeof target.clearStatus === 'function') target.clearStatus();
        else target.status = '';
      }

      const active = side.active?.[0] as
        | { moveSlots: Array<Record<string, unknown>> }
        | undefined;
      if (!active?.moveSlots) return false;
      active.moveSlots.push({
        move: 'Splash',
        id: 'splash',
        pp: 1,
        maxpp: 1,
        target: 'self',
        disabled: false,
        used: false,
      });
      suppressPhantomLines = true;
      phantomCleanupPending = true;
      void streams.omniscient.write('>p1 move splash');
      return true;
    } catch (err) {
      console.warn('[battle] item turn injection failed:', err);
      return false;
    }
  }

  return {
    events: () => eventQueue.iter(),
    nextP1Choice: () => choiceQueue.pull(),
    chooseMove: (slot: number) => {
      void streams.omniscient.write(`>p1 move ${slot}`);
    },
    chooseSwitch: (slot: number) => {
      void streams.omniscient.write(`>p1 switch ${slot}`);
    },
    snapshotP1Statuses,
    useItemTurn,
  };
}
